import React, { FC, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { MessageIcon } from '../../../../assets';
import { ChatBox, useFirestoreQuery } from '../../chat/components';
import { limit, orderBy, where } from 'firebase/firestore';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';

interface GroupChatProps {
  readonly roomID: string;
  readonly isVerticalMode: boolean;
}

export const GroupChat: FC<GroupChatProps> = ({ roomID, isVerticalMode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { firebaseUID } = useSelector((state: Store) => state.userReducer);
  const players = useSelector((state: Store) => state.inPlayReducer.players);

  // Firebase UIDs of everyone currently in this room (me + opponents). This
  // becomes the allowedUIDs array on every group-chat message and is used
  // both for reading (Firestore rules require array-contains my uid) and
  // writing (rules require the caller's uid to be present).
  const allowedUIDs = useMemo(() => {
    const uids = new Set<string>();
    if (firebaseUID) uids.add(firebaseUID);
    (players || []).forEach(p => {
      if (p.firebaseUID) uids.add(p.firebaseUID);
    });
    return Array.from(uids);
  }, [players, firebaseUID]);

  const queryConstraint = useMemo(
    () =>
      firebaseUID
        ? [
            orderBy('createdAt', 'desc'),
            limit(50),
            // Tighter than where('groupID','==', roomID): the user must be
            // in allowedUIDs to even see the docs. Firestore rules also
            // enforce this server-side.
            where('groupID', '==', roomID),
            where('allowedUIDs', 'array-contains', firebaseUID),
          ]
        : [],
    [roomID, firebaseUID]
  );

  const messages = useFirestoreQuery(queryConstraint);

  // Track how many messages the user has "acknowledged". We bump this every
  // time the popup closes so we only auto-open when *new* messages arrive.
  // Without this guard, every Firestore snapshot (which produces a new array
  // reference) would re-fire the effect and slam the popup back open after
  // the user closed it.
  const seenCountRef = useRef(messages.length);

  useEffect(() => {
    if (messages.length > seenCountRef.current) {
      setIsOpen(true);
    }
  }, [messages]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      if (!next) {
        // User closed the popup — anything currently visible is acknowledged.
        seenCountRef.current = messages.length;
      }
    },
    [messages.length]
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <MessageIcon
          fontSize="large"
          variant="outlined"
          sx={theme => ({ color: theme.palette.primary.main })}
        />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="mx-2 w-80 p-1 shadow-lg bg-slate-300 dark:bg-slate-600 overflow-auto border-none"
      >
        <ChatBox
          queryConstraint={queryConstraint}
          outerBoxProps={{
            className: 'flex flex-col',
            // Cap the popover height by the live keyboard inset so the
            // bottom-anchored input never slides under the keyboard.
            // `--kb-inset` is 0 when the keyboard is closed, so behaviour
            // is unchanged at rest. We use min(<intended height>, calc(100vh
            // - 100px - kb)) so the popover keeps its desired height when
            // there's room and shrinks only as much as the keyboard
            // demands. Vertical (portrait) caps at 400px to match the old
            // h-[400px] Tailwind value; horizontal caps at 500px which
            // matches the desktop lg:h-[500px] value and degrades naturally
            // on landscape phones where 100vh is small.
            style: {
              height: isVerticalMode
                ? 'min(400px, calc(100vh - 100px - var(--kb-inset, 0px)))'
                : 'min(500px, calc(100vh - 100px - var(--kb-inset, 0px)))',
              transition: 'height var(--kb-transition, 0ms linear)',
            },
          }}
          sendMessageProps={{
            roomID,
            allowedUIDs,
            messageFormProps: {
              height: 75,
            },
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
